import React, { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  formControlLabelClasses,
  Radio,
  radioClasses,
  RadioGroup,
  type RadioProps,
  Stack,
  styled,
  Typography,
  useTheme
} from '@mui/material';
import { mergeSx } from 'merge-sx';

import {
  ProcessResponseDTOStatus,
  ProcessTalentInvitationResponseSendMode,
  ProcessUpdateDtoSendModesItem,
  TalentDTOStatus
} from '@/api/generated';
// import ChatIcon from '@/assets/icons/chat.svg?react';
import LinkIcon from '@/assets/icons/link.svg?react';
import MailIcon from '@/assets/icons/mail.svg?react';
// import WhatsappIcon from '@/assets/icons/whatsapp.svg?react';
import LoadingButton from '@/components/LoadingButton';

import { useTalentProcessContext } from './TalentProcessContext';

const InvitationModalContent = ({ onCopyLink }: { onCopyLink: () => void }) => {
  const { formState } = useFormContext();
  const { t } = useTranslation();
  const { spacing, palette } = useTheme();
  const { talent, process } = useTalentProcessContext();

  const isSendButtonDisabled =
    !formState.isDirty ||
    formState.isSubmitSuccessful ||
    talent?.status === TalentDTOStatus.REFUSED ||
    talent?.status === TalentDTOStatus.COMPLETED ||
    process?.status !== ProcessResponseDTOStatus.ACTIVE;

  return (
    <>
      <FormControl>
        <Controller
          name="sendMode"
          defaultValue={null}
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup
              row
              sx={{
                justifyContent: 'space-between',
                paddingX: spacing(2),
                paddingY: spacing(3)
              }}
              {...field}
            >
              {/* <InvitationControl
                disabled
                icon={<WhatsappIcon display="block" width={spacing(3)} height={spacing(3)} />}
                label={t('common:whatsapp')}
                value={ProcessTalentInvitationResponseSendMode.WHATSAPP}
              />
              <InvitationControl
                disabled
                icon={<ChatIcon display="block" width={spacing(3)} height={spacing(3)} />}
                label={t('common:sms')}
                value={ProcessTalentInvitationResponseSendMode.TEXT_MESSAGE}
              /> */}
              <InvitationControl
                disabled={!process.sendModes?.includes(ProcessUpdateDtoSendModesItem.EMAIL)}
                icon={<MailIcon display="block" width={spacing(3)} height={spacing(3)} />}
                label={t('common:email')}
                value={ProcessTalentInvitationResponseSendMode.EMAIL}
              />
            </RadioGroup>
          )}
        />
      </FormControl>
      <Divider sx={{ borderColor: palette.grey[400] }} />
      <Stack
        direction="row"
        sx={mergeSx({ padding: spacing(2.5, 2, 2), gap: spacing(1.5), justifyContent: 'space-between' })}
      >
        <Button variant="text" onClick={onCopyLink} disabled={!formState.isSubmitSuccessful} startIcon={<LinkIcon />}>
          {t('processes:layouts.process.sendInvitation.copyLink')}
        </Button>
        <LoadingButton
          autoFocus
          type="submit"
          color="primary"
          variant="contained"
          disabled={isSendButtonDisabled}
          isLoading={formState.isSubmitting}
        >
          {formState.isSubmitSuccessful && !formState.isDirty
            ? t('processes:layouts.process.sendInvitation.sent')
            : t('processes:layouts.process.sendInvitation.send')}
        </LoadingButton>
      </Stack>
    </>
  );
};

const InvitationControl = ({
  icon,
  label,
  value,
  disabled
}: {
  icon: ReactNode;
  label: ReactNode;
  value: ProcessTalentInvitationResponseSendMode;
  disabled?: boolean;
}) => {
  const { spacing, palette } = useTheme();

  return (
    <FormControlLabel
      value={value}
      name="sendMode"
      labelPlacement="bottom"
      label={
        <Typography
          variant="body2"
          sx={{ color: disabled ? palette.grey[500] : palette.grey[900], textAlign: 'center' }}
        >
          {label}
        </Typography>
      }
      sx={{ margin: 0, alignItems: 'center', width: spacing(10.75) }}
      control={<InvitationModeRadio disabled={disabled} icon={icon} />}
    />
  );
};

const InvitationModeRadio = styled((props: RadioProps) => (
  <Radio
    {...props}
    disableRipple
    icon={<InvitationModeIcon icon={props.icon} />}
    checkedIcon={<InvitationModeIcon icon={props.icon} />}
  />
))(({ theme: { spacing } }) => ({ padding: 0, marginBottom: spacing(1) }));

const InvitationModeIcon = styled(({ icon }: { icon: ReactNode; checked?: boolean }) => {
  const { spacing, palette } = useTheme();

  return (
    <Box
      sx={{
        padding: spacing(1),
        border: `1px solid ${palette.grey[300]}`,
        borderRadius: spacing(0.5),
        color: palette.primary.main,

        [`.${formControlLabelClasses.root}:hover &`]: {
          backgroundColor: palette.grey[200],
          borderColor: palette.grey[400]
        },

        [`.${radioClasses.root}.${radioClasses.checked} &`]: {
          backgroundColor: palette.primary[100],
          borderColor: palette.primary.main
        },

        [`.${radioClasses.root}.${radioClasses.disabled} &`]: {
          backgroundColor: palette.grey[200],
          borderColor: palette.grey[500],
          color: palette.grey[500]
        }
      }}
    >
      {icon}
    </Box>
  );
})({});

export default InvitationModalContent;
